// Keyword im Tracking (Nico, 12.09.2026).
//
// Google gibt das auslösende Keyword beim Anzeigenklick mit, wenn im Konto
// `utm_term={keyword}` im Final-URL-Suffix steht. Ohne das lässt sich nicht
// auswerten, bei welchem Keyword die Leute nach zehn Sekunden abspringen.
//
// Warum mit Vorschau und nicht still gesetzt: Es ist ein Eingriff ins laufende
// Werbekonto. Was danach im Konto steht, muss man vorher lesen können — gerade
// dort, wo schon ein Suffix vorhanden ist und ergänzt wird.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2, Check, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import { getAdsTracking, setzeAdsTracking } from '@/lib/analytics-api';

export default function KeywordTracking() {
  const qc = useQueryClient();
  const [aufgeklappt, setAufgeklappt] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ['ads-tracking'], queryFn: getAdsTracking });

  const setzen = useMutation({
    mutationFn: (projectId?: string) => setzeAdsTracking(projectId),
    onSuccess: (r) => {
      if (r.gesetzt.length) toast.success(`Keyword-Tracking gesetzt: ${r.gesetzt.map((g) => g.kunde || g.projekt).join(', ')}`);
      if (r.fehler.length) toast.error(`${r.fehler.length} Konten nicht geändert: ${r.fehler[0].grund}`);
      qc.invalidateQueries({ queryKey: ['ads-tracking'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
        <Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> Werbekonten werden gelesen …
      </div>
    );
  }
  if (!data || !data.projekte.length) return null;

  const offene = data.projekte.filter((p) => !p.hat_keyword && !p.fehler);
  const fertig = data.projekte.filter((p) => p.hat_keyword);
  const kaputt = data.projekte.filter((p) => p.fehler);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
            <KeyRound className="h-4 w-4 text-gray-400" /> Keyword im Tracking
          </h2>
          <p className="mt-1 max-w-2xl text-xs text-gray-500">
            Google hängt beim Anzeigenklick das auslösende Keyword an die Adresse, wenn im Konto{' '}
            <code className="rounded bg-gray-100 px-1 py-0.5 text-[11px]">utm_term=&#123;keyword&#125;</code> steht.
            Erst damit lässt sich sehen, bei welchem Keyword die Leute sofort wieder abspringen.
            Ein vorhandener Suffix wird ergänzt, nicht überschrieben.
          </p>
        </div>
        {offene.length > 0 && (
          <Button variant="primary" size="sm" loading={setzen.isPending} onClick={() => setzen.mutate(undefined)}>
            Für {offene.length} {offene.length === 1 ? 'Konto' : 'Konten'} setzen
          </Button>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {fertig.length > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
            <Check className="h-3.5 w-3.5" /> {fertig.length} {fertig.length === 1 ? 'Konto liefert' : 'Konten liefern'} das Keyword
          </span>
        )}
        {offene.length > 0 && (
          <button
            type="button"
            onClick={() => setAufgeklappt((o) => !o)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100"
          >
            <AlertTriangle className="h-3.5 w-3.5" /> {offene.length} noch nicht — {aufgeklappt ? 'zuklappen' : 'ansehen'}
          </button>
        )}
        {kaputt.length > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-2.5 py-1 text-xs font-medium text-red-800">
            <AlertTriangle className="h-3.5 w-3.5" /> {kaputt.length} nicht lesbar
          </span>
        )}
      </div>

      {aufgeklappt && offene.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200">
          {offene.map((p) => (
            <li key={p.project_id} className="flex flex-wrap items-start justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-900">
                  {p.kunde || p.projekt} <span className="font-normal text-gray-500">· {p.projekt}</span>
                </div>
                <div className="mt-1 break-all text-xs text-gray-500">
                  {p.konto_suffix
                    ? <>jetzt <code className="rounded bg-gray-100 px-1">{p.konto_suffix}</code> → danach <code className="rounded bg-blue-50 px-1 text-blue-800">{p.konto_neu}</code></>
                    : <>bisher kein Suffix → danach <code className="rounded bg-blue-50 px-1 text-blue-800">{p.konto_neu}</code></>}
                </div>
                {p.kampagnen_mit_eigenem.length > 0 && (
                  <div className="mt-1 text-xs text-amber-700">
                    {p.kampagnen_mit_eigenem.length} {p.kampagnen_mit_eigenem.length === 1 ? 'Kampagne hat' : 'Kampagnen haben'} einen eigenen Suffix —
                    der schlägt den des Kontos und wird deshalb mitgesetzt.
                  </div>
                )}
              </div>
              <Button variant="outline" size="sm" loading={setzen.isPending && setzen.variables === p.project_id} onClick={() => setzen.mutate(p.project_id)}>
                Setzen
              </Button>
            </li>
          ))}
        </ul>
      )}

      {kaputt.length > 0 && (
        <ul className="mt-3 space-y-1">
          {kaputt.map((p) => (
            <li key={p.project_id} className="text-xs text-red-700">{p.kunde || p.projekt}: {p.fehler}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
